import React from 'react';

/**
 * Loading spinner component with various sizes and styles
 * 
 * @param {Object} props
 * @param {string} props.size - Spinner size: 'sm', 'md', 'lg', 'xl' (default: 'md')
 * @param {string} props.color - Tailwind color class (default: 'border-primary-600')
 * @param {boolean} props.fullScreen - Center in full viewport (default: false)
 * @param {boolean} props.overlay - Show with overlay background (default: false)
 * @param {string} props.text - Optional loading text to display
 * @param {string} props.className - Additional classes
 * 
 * @example
 * // Simple inline spinner
 * <LoadingSpinner size="sm" />
 * 
 * // Full page loading
 * <LoadingSpinner fullScreen text="Loading..." />
 * 
 * // Overlay loading (for sections)
 * <LoadingSpinner overlay />
 */
const LoadingSpinner = ({
  size = 'md',
  color = 'border-primary-600',
  fullScreen = false,
  overlay = false,
  text,
  className = ''
}) => {
  // Size mappings
  const sizeClasses = {
    sm: 'h-4 w-4 border-2',
    md: 'h-8 w-8 border-2',
    lg: 'h-12 w-12 border-3',
    xl: 'h-16 w-16 border-4',
    '2xl': 'h-32 w-32 border-4'
  };

  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg',
    '2xl': 'text-xl'
  };

  const spinner = (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <div 
        className={`
          animate-spin rounded-full 
          border-b-transparent 
          ${sizeClasses[size] || sizeClasses.md} 
          ${color}
        `}
      />
      {text && (
        <p className={`mt-2 text-gray-600 dark:text-gray-400 ${textSizes[size]}`}>
          {text}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        {spinner}
      </div>
    );
  }

  if (overlay) {
    return (
      <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 flex items-center justify-center z-10 rounded-lg">
        {spinner}
      </div>
    );
  }

  return spinner;
};

/**
 * Inline loading indicator for buttons
 */
export const ButtonSpinner = ({ className = '' }) => (
  <div 
    className={`animate-spin rounded-full h-4 w-4 border-2 border-white border-b-transparent ${className}`}
  />
);

/**
 * Skeleton loading placeholder
 */
export const Skeleton = ({ 
  width = 'w-full', 
  height = 'h-4', 
  rounded = 'rounded',
  className = '' 
}) => (
  <div 
    className={`animate-pulse bg-gray-200 dark:bg-gray-700 ${width} ${height} ${rounded} ${className}`}
  />
);

export default LoadingSpinner;
